import React, { useMemo, useState } from 'react';

import { format } from 'date-fns';

import {
  DataTable,
  Column,
  Toggle,
  Flex,
  LssProp,
  Skeleton,
  Spinner,
  SortOrder,
  SortProps
} from '@lucidhq/lucidium';

import { useErrorDialogProvider } from '~/components/Shared/ErrorDialogProvider';
import { useUpdateIPMatching } from '~/components/Shared/hooks/useMutateIPMatching';
import { t } from '~/utils/i18n';
import { findMatchingIdLists } from '~/utils/ipMatching';

import { IPMatchedID, useIPMatchingContext } from '../IPMatchingProvider';
import { IDsTableColHead } from './IDsTableColHead';
import { NoMatchedIDsMessage } from './NoMatchedIDsMessage';

const tableContainerLss: LssProp = {
  width: '100%',
  p: 'lg',
  maxHeight: '78rem',
  overflow: 'auto'
};

const AUTO = 'Auto';
const MANUAL = 'Manual';
const SORT_ORDER = 'desc';
const SORT_BY = 'added_at';

const getStatusLabel = (cellData: string | null) => {
  return cellData === 'manual' ? MANUAL : AUTO;
};

export const IDsTable = () => {
  const [updatedIds, setUpdatedIds] = useState<IPMatchedID[]>([]);
  const [isUpdatingId, setUpdating] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string>('');
  const [dataSortOrder, setSortOrder] = useState<SortOrder>(SORT_ORDER);
  const [dataSortBy, setSortBy] = useState<keyof IPMatchedID>(SORT_BY);

  const {
    campaign,
    matchedIds,
    isLoadingIPMatching,
    rootLevelEnablement
  } = useIPMatchingContext();
  const { mutate } = useUpdateIPMatching(campaign.campaign_id);
  const { setError } = useErrorDialogProvider();

  const ipIds = useMemo(() => {
    const hasMatchingIds = findMatchingIdLists(updatedIds, matchedIds);

    return !!(updatedIds.length && hasMatchingIds) ? updatedIds : matchedIds;
  }, [matchedIds, updatedIds]);

  const handleUpdateId = (id: any) => {
    const payload = { ids: [id] };
    setUpdating(true);

    mutate(payload, {
      onSuccess: (data: any) => {
        const updatedId = !!data?.items?.length ? data.items[0] : {};

        const newIds: IPMatchedID[] = ipIds.map(item =>
          item.lookup_key === updatedId?.lookup_key ? updatedId : item
        );

        setUpdatedIds(newIds);
        setUpdating(false);
      },
      onError: () => {
        setUpdating(false);
        setError(t('errors.generalApiError'));
      }
    });
  };

  const handleSort = (sortOptions: SortProps<IPMatchedID>) => {
    const sortBy = sortOptions?.sortBy ?? SORT_BY;
    const sortOrder = sortOptions?.sortOrder ?? SORT_ORDER;

    const sortedIpIds = [...ipIds].sort(
      (a: IPMatchedID, b: IPMatchedID): number => {
        const aSort = a[sortBy];
        const bSort = b[sortBy];
        if (typeof aSort === 'string' && typeof bSort === 'string') {
          if (sortOrder !== 'asc') {
            return bSort.localeCompare(aSort, 'en', {
              sensitivity: 'base'
            });
          }

          return aSort.localeCompare(bSort, 'en', {
            sensitivity: 'base'
          });
        }

        if (aSort < bSort) return sortOrder === 'asc' ? -1 : 1;
        if (aSort > bSort) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      }
    );

    setSortOrder(sortOrder);
    setSortBy(sortBy);
    setUpdatedIds(sortedIpIds);
  };

  if (isLoadingIPMatching)
    return (
      <Skeleton
        ariaLabel="rect"
        variant="rect"
        width="100%"
        height="60rem"
        animation="wave"
      />
    );

  return (
    <>
      {!!ipIds?.length ? (
        <Flex lss={tableContainerLss}>
          <DataTable
            data={ipIds}
            sortBy={dataSortBy}
            sortOrder={dataSortOrder}
            onSort={handleSort}
            rowKeySelector="lookup_key"
            hasFooterRow={false}
          >
            <Column<IPMatchedID, 'ip_matching_type'>
              dataKey="ip_matching_type"
              label={t('ipMatching.labels.idType')}
              cellRenderer={cellData => {
                return cellData.includes('placement')
                  ? t('ipMatching.labels.placement')
                  : t('ipMatching.labels.site');
              }}
            />
            <Column<IPMatchedID, 'ip_matching_id'>
              dataKey="ip_matching_id"
              label={t('ipMatching.labels.ipID')}
            />
            <Column<IPMatchedID, 'enablement_status'>
              dataKey="enablement_status"
              label={
                <IDsTableColHead
                  label={t('ipMatching.labels.ipMatch')}
                  tooltip={t('ipMatching.tooltips.ipMatch')}
                />
              }
              cellRenderer={cellData => getStatusLabel(cellData)}
            />
            <Column<IPMatchedID, 'added_at'>
              dataKey="added_at"
              label={
                <IDsTableColHead
                  label={t('ipMatching.labels.dateAdded')}
                  tooltip={t('ipMatching.tooltips.dateAdded')}
                />
              }
              cellRenderer={cellData =>
                format(new Date(cellData), 'yyyy/dd/MM')
              }
            />
            <Column<IPMatchedID, 'is_enabled'>
              dataKey="is_enabled"
              label={
                <IDsTableColHead
                  label={t('ipMatching.labels.status')}
                  tooltip={t('ipMatching.tooltips.status')}
                />
              }
              cellRenderer={(_, rowData) => {
                const isUpdatingRow =
                  isUpdatingId && selectedId === rowData?.lookup_key;

                return (
                  <Flex lss={{ flexDirection: 'row', gap: '0.5rem' }}>
                    <Toggle
                      value={rowData.is_enabled || false}
                      scale="sm"
                      disabled={!rootLevelEnablement}
                      lss={{ borderRadius: 'lg' }}
                      onChange={() => {
                        const id = {
                          ip_matching_type: rowData.ip_matching_type,
                          ip_matching_id: rowData.ip_matching_id,
                          is_enabled: !rowData.is_enabled
                        };

                        setSelectedId(rowData.lookup_key);
                        handleUpdateId(id);
                      }}
                    />
                    {isUpdatingRow && (
                      <Spinner size="sm" lss={{ mx: 'none' }} />
                    )}
                  </Flex>
                );
              }}
            />
          </DataTable>
        </Flex>
      ) : (
        <NoMatchedIDsMessage />
      )}
    </>
  );
};
